class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);
    this.updatePosition = this.updatePosition.bind(this);
    this.onViewportChange = this.handleViewportChange.bind(this);
    this.focusTimer = null;
    this.onTransitionEnd = null;
    this.positionFrame = null;
    this.positionTimer = null;
    this.positionResetTimer = null;
    this.mobileQuery = window.matchMedia('(max-width: 749px)');
    this.touchState = null;
    this.onTouchStart = this.handleTouchStart.bind(this);
    this.onTouchMove = this.handleTouchMove.bind(this);
    this.onTouchEnd = this.handleTouchEnd.bind(this);
    
    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelectorAll('.cart-notification__close, .cart-notification__continue-button').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );
    this.notification.addEventListener('touchstart', this.onTouchStart, { passive: true });
    this.notification.addEventListener('touchmove', this.onTouchMove, { passive: false });
    this.notification.addEventListener('touchend', this.onTouchEnd, { passive: true });
    this.notification.addEventListener('touchcancel', this.onTouchEnd, { passive: true });
  }

  open() {
    window.clearTimeout(this.positionResetTimer);
    this.positionResetTimer = null;
    this.resetDragPosition();
    this.updatePosition();
    this.classList.add('animate', 'active');
    this.notification.classList.add('animate', 'active');
    document.body.classList.add('cart-notification-open');

    const focusNotification = () => {
      window.clearTimeout(this.focusTimer);
      if (this.onTransitionEnd) {
        this.notification.removeEventListener('transitionend', this.onTransitionEnd);
        this.onTransitionEnd = null;
      }
      if (!this.notification.classList.contains('active')) return;
      trapFocus(this.notification, this.notification, { preventScroll: true });
    };

    if (this.onTransitionEnd) {
      this.notification.removeEventListener('transitionend', this.onTransitionEnd);
    }

    this.onTransitionEnd = (evt) => {
      if (evt.target === this.notification) focusNotification();
    };

    this.notification.addEventListener('transitionend', this.onTransitionEnd);
    this.focusTimer = window.setTimeout(focusNotification, 320);

    document.body.addEventListener('click', this.onBodyClick);
    this.addPositionListeners();
    this.schedulePositionUpdate();
    this.settlePositionUpdate();
  }

  close() {
    window.clearTimeout(this.focusTimer);
    if (this.onTransitionEnd) {
      this.notification.removeEventListener('transitionend', this.onTransitionEnd);
      this.onTransitionEnd = null;
    }

    this.classList.remove('active');
    this.notification.classList.remove('active');
    this.classList.remove('is-dragging');
    document.body.classList.remove('cart-notification-open');

    document.body.removeEventListener('click', this.onBodyClick);
    this.removePositionListeners();
    this.clearScheduledPositionUpdates();

    window.clearTimeout(this.positionResetTimer);
    this.positionResetTimer = window.setTimeout(() => {
      if (!this.notification.classList.contains('active')) {
        this.resetPosition();
        this.resetDragPosition();
      }
    }, 280);

    removeTrapFocus();
    this.activeElement?.focus({ preventScroll: true });
  }

  updatePosition() {
    if (!this.mobileQuery.matches) {
      this.resetPosition();
      return;
    }

    this.style.setProperty('--dr-cart-notification-top-offset', `${this.getHeaderOffset()}px`);
    this.style.setProperty('--dr-cart-notification-bottom-offset', `${this.getFooterOffset()}px`);
  }

  resetPosition() {
    this.style.removeProperty('--dr-cart-notification-top-offset');
    this.style.removeProperty('--dr-cart-notification-bottom-offset');
  }

  getHeaderOffset() {
    const viewportHeight = this.getViewportHeight();
    const maxOffset = Math.max(0, viewportHeight - 160);
    let offset = 0;

    this.getHeaderCandidates().forEach((element) => {
      offset = Math.max(offset, this.getVisibleElementBottom(element, viewportHeight));
    });

    return Math.min(offset, maxOffset);
  }

  getHeaderCandidates() {
    const selectors = [
      '#shopify-section-header',
      '#shopify-section-header > .header-wrapper',
      '#shopify-section-header sticky-header.header-wrapper',
      '#shopify-section-header .header',
      '#shopify-section-header .header__mobile-search-row',
      '#shopify-section-header .header__mobile-search-form',
    ];
    const candidates = [];

    selectors.forEach((selector) => {
      document.querySelectorAll(selector).forEach((element) => {
        if (!candidates.includes(element)) candidates.push(element);
      });
    });

    return candidates;
  }

  getVisibleElementBottom(element, viewportHeight) {
    const styles = window.getComputedStyle(element);

    if (styles.display === 'none' || styles.visibility === 'hidden') return 0;

    const rect = element.getBoundingClientRect();

    if (rect.width <= 0 || rect.height <= 0 || rect.bottom <= 0 || rect.top >= viewportHeight) {
      return 0;
    }

    return Math.max(0, Math.ceil(Math.min(rect.bottom, viewportHeight)));
  }

  getFooterOffset() {
    if (
      document.body.classList.contains('product-card-drawer-open') ||
      document.querySelector('.product-card-drawer-shell.is-open')
    ) {
      return 0;
    }

    const footerNav = document.querySelector('[data-footer-mobile-nav], .dr-footer-mobile-nav');

    if (!footerNav) return 0;

    const styles = window.getComputedStyle(footerNav);

    if (styles.display === 'none' || styles.visibility === 'hidden') return 0;

    const footerRect = footerNav.getBoundingClientRect();
    const viewportHeight = this.getViewportHeight();

    if (
      footerRect.width <= 0 ||
      footerRect.height <= 0 ||
      footerRect.bottom <= 0 ||
      footerRect.top >= viewportHeight
    ) {
      return 0;
    }

    return Math.min(
      viewportHeight,
      Math.max(0, Math.ceil(viewportHeight - Math.max(footerRect.top, 0)))
    );
  }

  getViewportHeight() {
    if (window.visualViewport && window.visualViewport.height) {
      return window.visualViewport.height;
    }

    return window.innerHeight || document.documentElement.clientHeight || 0;
  }

  handleViewportChange() {
    if (!this.notification.classList.contains('active')) return;

    this.schedulePositionUpdate();
    this.settlePositionUpdate();
  }

  addPositionListeners() {
    window.addEventListener('resize', this.onViewportChange, { passive: true });
    window.addEventListener('orientationchange', this.onViewportChange, { passive: true });

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', this.onViewportChange, { passive: true });
    }
  }

  removePositionListeners() {
    window.removeEventListener('resize', this.onViewportChange);
    window.removeEventListener('orientationchange', this.onViewportChange);

    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.onViewportChange);
    }
  }

  schedulePositionUpdate() {
    if (this.positionFrame) window.cancelAnimationFrame(this.positionFrame);

    this.positionFrame = window.requestAnimationFrame(() => {
      this.positionFrame = null;
      this.updatePosition();
    });
  }

  settlePositionUpdate() {
    window.clearTimeout(this.positionTimer);

    this.positionTimer = window.setTimeout(() => {
      this.positionTimer = null;
      this.updatePosition();
    }, 180);
  }

  clearScheduledPositionUpdates() {
    if (this.positionFrame) {
      window.cancelAnimationFrame(this.positionFrame);
      this.positionFrame = null;
    }

    window.clearTimeout(this.positionTimer);
    this.positionTimer = null;
  }

  handleTouchStart(event) {
    const touch = event.touches && event.touches[0];

    if (!touch || !this.mobileQuery.matches || !this.notification.classList.contains('active')) return;

    const rect = this.notification.getBoundingClientRect();

    this.touchState = {
      startX: touch.clientX,
      startY: touch.clientY,
      lastX: touch.clientX,
      lastY: touch.clientY,
      startTime: Date.now(),
      mode: null,
      startedOnDragArea: rect.height > 0 && touch.clientY >= rect.bottom - 72,
      startedOnInteractive: Boolean(event.target.closest('a, button, input, select, textarea, label')),
    };
  }

  handleTouchMove(event) {
    const touch = event.touches && event.touches[0];
    let dx;
    let dy;

    if (!touch || !this.touchState) return;

    dx = touch.clientX - this.touchState.startX;
    dy = touch.clientY - this.touchState.startY;
    this.touchState.lastX = touch.clientX;
    this.touchState.lastY = touch.clientY;

    if (!this.touchState.mode && this.shouldStartDrawerDrag(dx, dy)) {
      this.touchState.mode = 'drawer';
      this.classList.add('is-dragging');
    }

    if (this.touchState.mode !== 'drawer') return;

    event.preventDefault();
    this.notification.style.setProperty(
      '--dr-cart-notification-drag-y',
      `${Math.min(0, dy)}px`
    );
  }

  handleTouchEnd() {
    const touchState = this.touchState;
    let dy;
    let velocity;

    if (!touchState) return;

    dy = touchState.lastY - touchState.startY;
    velocity = dy / Math.max(Date.now() - touchState.startTime, 1);

    if (touchState.mode === 'drawer') {
      this.classList.remove('is-dragging');

      if (dy < -88 || velocity < -0.55) {
        this.close();
      } else {
        this.resetDragPosition();
      }
    }

    this.touchState = null;
  }

  shouldStartDrawerDrag(dx, dy) {
    return (
      this.touchState &&
      this.touchState.startedOnDragArea &&
      !this.touchState.startedOnInteractive &&
      dy < -8 &&
      Math.abs(dy) > Math.abs(dx)
    );
  }

  resetDragPosition() {
    this.touchState = null;
    this.classList.remove('is-dragging');
    this.notification.style.setProperty('--dr-cart-notification-drag-y', '0px');
  }

  renderContents(parsedState) {
    this.productId = parsedState.id;
    this.productIds = this.getParsedProductIds(parsedState);
    let cartItemCount = typeof parsedState.item_count === 'number' ? parsedState.item_count : null;

    this.getSectionsToRender().forEach((section => {
      const sectionHtml = parsedState.sections && parsedState.sections[section.id];
      const target = document.getElementById(section.id);

      if (!sectionHtml || !target) return;

      target.innerHTML = this.getRenderedSectionHTML(sectionHtml, section);

      if (section.id === 'cart-icon-bubble' && window.DubiaCartCounts) {
        const renderedCount = window.DubiaCartCounts.extractFromSectionHtml(sectionHtml);
        if (renderedCount !== null) cartItemCount = renderedCount;
      }
    }));

    this.syncCartCount(cartItemCount);

    if (this.header) this.header.reveal();
    this.open();
  }

  getParsedProductIds(parsedState) {
    const productIds = Array.isArray(parsedState.productIds) ? parsedState.productIds : [];

    return productIds.map((id) => String(id)).filter(Boolean);
  }

  syncCartCount(cartItemCount) {
    let count = typeof cartItemCount === 'number' ? cartItemCount : null;

    if (count === null && window.DubiaCartCounts) {
      count = window.DubiaCartCounts.extractFromRoot(document.getElementById('cart-icon-bubble'));
    }

    if (count !== null && window.DubiaCartCounts) {
      window.DubiaCartCounts.update(count);
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        preserveElement: true,
        selector: `#cart-notification-product-${this.productId}`,
        selectors: this.productIds && this.productIds.length > 1
          ? this.productIds.map((id) => `#cart-notification-product-${id}`)
          : null,
      },
      {
        id: 'cart-notification-button'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getRenderedSectionHTML(html, section) {
    if (section.selectors && section.selectors.length) {
      return this.getSectionInnerHTMLForSelectors(html, section.selectors);
    }

    if (section.preserveElement) {
      return this.getSectionOuterHTML(html, section.selector);
    }

    return this.getSectionInnerHTML(html, section.selector);
  }

  getSectionInnerHTMLForSelectors(html, selectors) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const seen = new Set();

    return selectors
      .map((selector) => doc.querySelector(selector))
      .filter(Boolean)
      .filter((element) => {
        const key = element.id || element.outerHTML;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .map((element) => element.outerHTML)
      .join('');
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const element = new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);

    return element ? element.innerHTML : '';
  }

  getSectionOuterHTML(html, selector = '.shopify-section') {
    const element = new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);

    return element ? element.outerHTML : '';
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('.cart-notification')) {
      const disclosure = target.closest('details-disclosure');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);
